import { useState } from 'react';
import { Mail, CheckCircle, Loader2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterSubscribe = ({ variant = 'inline' }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success', 'error', 'exists'
  const [showForm, setShowForm] = useState(variant === 'inline');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || isLoading) return;

    setIsLoading(true);
    setStatus(null);

    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: name || null })
      });

      const data = await response.json();

      if (data.status === 'subscribed' || data.status === 'resubscribed') {
        setStatus('success');
        setEmail('');
        setName('');
      } else if (data.status === 'exists') {
        setStatus('exists');
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Subscribe error:', error);
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Floating variant (bottom-left popup)
  if (variant === 'floating') {
    return (
      <>
        {/* Trigger Button */}
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="fixed bottom-20 left-4 z-40 bg-primary hover:bg-primary/90 text-white px-4 py-3 rounded-full shadow-lg transition-all duration-300 hover:scale-105 flex items-center gap-2"
            data-testid="newsletter-trigger"
          >
            <Mail className="w-5 h-5" />
            <span className="text-sm font-medium">Newsletter</span>
          </button>
        )}

        {/* Popup Form */}
        {showForm && (
          <div className="fixed bottom-20 left-4 z-40 w-80 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-5" data-testid="newsletter-popup">
            {/* Header */}
            <div className="bg-primary p-4 text-white relative">
              <button
                onClick={() => setShowForm(false)}
                className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              <h3 className="font-bold text-lg">Stay Updated</h3>
              <p className="text-white/80 text-sm">Get monthly trade insights & deals</p>
            </div>

            {/* Content */}
            <div className="p-4">
              {status === 'success' ? (
                <div className="text-center py-4">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="font-semibold text-charcoal">You're subscribed!</p>
                  <p className="text-sm text-gray-500">Check your inbox for a welcome email.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Your name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                  />
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                    data-testid="newsletter-email-input"
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !email}
                    className="w-full bg-primary text-white py-2.5 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="newsletter-submit"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Subscribe
                      </>
                    )}
                  </button>
                  {status === 'exists' && (
                    <p className="text-sm text-amber-600 text-center">You&apos;re already subscribed!</p>
                  )}
                  {status === 'error' && (
                    <p className="text-sm text-red-600 text-center">Something went wrong. Please try again.</p>
                  )}
                </form>
              )}
              <p className="text-[10px] text-gray-400 text-center mt-3">
                No spam. Unsubscribe anytime.
              </p>
            </div>
          </div>
        )}
      </>
    );
  }

  // Inline variant (for footer or sections)
  return (
    <div className="w-full max-w-md" data-testid="newsletter-inline">
      {status === 'success' ? (
        <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
          <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-green-800">Successfully subscribed!</p>
            <p className="text-sm text-green-600">Check your inbox for a welcome email.</p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
            data-testid="newsletter-email-input-inline"
          />
          <button
            type="submit"
            disabled={isLoading || !email}
            className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="newsletter-submit-inline"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Subscribe'
            )}
          </button>
        </form>
      )}
      {status === 'exists' && (
        <p className="text-sm text-amber-600 mt-2">You&apos;re already subscribed!</p>
      )}
      {status === 'error' && (
        <p className="text-sm text-red-600 mt-2">Something went wrong. Please try again.</p>
      )}
    </div>
  );
};

export default NewsletterSubscribe;
